/**
 * Dictionnaire i18n du silo location de véhicules (catalogue + carte + form).
 *
 * Catalogue EN `/en/location/vehicules` : FR et EN rendent les mêmes composants
 * (CatalogueSection, VehicleCard, QuickBookingForm) ; le "chrome" vient d'ici
 * via `locale`. Défaut `fr` → zéro régression FR.
 *
 * Important : le pattern téléphone EN est PERMISSIF (international) — le pattern
 * FR strict rejetterait les numéros étrangers, soit précisément les leads
 * internationaux qu'on cible. La logique de soumission ne change pas.
 */
import type { Locale } from "@/config/i18n";
import { type I18n } from "@/config/localeText";
import { PHONE_PATTERNS } from "@/config/forms";

// Valeurs enum des specs (affichées sur la carte). Finies → map directe.
const GEARBOX_EN: Record<string, string> = {
  Automatique: "Automatic",
  Manuelle: "Manual",
};
const FUEL_EN: Record<string, string> = {
  Essence: "Petrol",
  Diesel: "Diesel",
  Hybride: "Hybrid",
  Électrique: "Electric",
};

export const localizeGearbox = (value: string, locale: Locale): string =>
  locale === "en" ? (GEARBOX_EN[value] ?? value) : value;
export const localizeFuel = (value: string, locale: Locale): string =>
  locale === "en" ? (FUEL_EN[value] ?? value) : value;

/** Base de la page catalogue selon la locale (liens fiche, form, href). */
export const vehiclesBasePath = (locale: Locale): string =>
  locale === "en" ? "/en/location/vehicules" : "/location/vehicules";

export type VehiclesUi = {
  durationLabels: {
    oneToThreeDays: string;
    fourToSixDays: string;
    sevenPlusDays: string;
  };
  page: {
    crumbHome: string;
    crumbRental: string;
    crumbVehicles: string;
    eyebrow: string;
    h1: string;
    intro: string;
    cta: string;
    faqHeading: string;
    faq: { q: string; a: string }[];
    footerLinks: { label: string; href: string }[];
  };
  catalogue: {
    back: string;
    backHref: string;
    title: string;
    intro: string;
    filters: string;
    closeFilter: string;
    brands: string;
    priceLabel: string;
    searchPlaceholder: string;
    sortedBy: string;
    sortOptions: Record<
      "date-desc" | "date-asc" | "price-desc" | "price-asc" | "popularity",
      string
    >;
    view2: string;
    view3: string;
    available: (n: number) => string;
    emptyTitle: string;
    emptyText: string;
    reset: string;
    bookThis: string;
  };
  card: {
    newBadge: string;
    driverBadge: string;
    reserve: string;
    fromHourly: (price: string) => string;
    perDay: string;
    kmPerDay: (km: number) => string;
    detailsAria: (name: string) => string;
  };
  form: {
    eyebrow: string;
    heading: string;
    intro: string;
    selectedModel: string;
    perDay: string;
    priceForDuration: string;
    seeCatalogue: string;
    seeModel: string;
    fullName: string;
    email: string;
    phone: string;
    phonePlaceholder: string;
    vehicle: string;
    selectModel: string;
    duration: string;
    date: string;
    message: string;
    messagePlaceholder: string;
    submit: string;
    sending: string;
    success: string;
    error: string;
    v: {
      nameReq: string;
      emailReq: string;
      phoneReq: string;
      phoneInvalid: string;
      vehicleReq: string;
      durationReq: string;
      messageReq: string;
    };
    phonePattern: RegExp;
    /** Préfixe du sujet de l'email équipe (flag client anglophone en EN). */
    subjectPrefix: string;
  };
};

// Motifs téléphone centralisés (@/config/forms) — FR strict / international
// permissif. Mêmes RegExp qu'avant, octet pour octet.
const FR_PHONE = PHONE_PATTERNS.fr;
const INTL_PHONE = PHONE_PATTERNS.intl;

export const VEHICLES_UI: I18n<VehiclesUi> = {
  fr: {
    durationLabels: {
      oneToThreeDays: "1 a 3 jours",
      fourToSixDays: "4 a 6 jours",
      sevenPlusDays: "7 jours et +",
    },
    page: {
      crumbHome: "Accueil",
      crumbRental: "Location",
      crumbVehicles: "Véhicules",
      eyebrow: "Catalogue premium",
      h1: "Location de véhicules premium à Cannes, Nice et Monaco",
      intro:
        "Comparez rapidement nos catégories, choisissez le modèle adapté à votre contexte (business, séjour, événement), puis réservez avec un accompagnement humain.",
      cta: "Choisir un véhicule et réserver",
      faqHeading: "FAQ location de véhicules",
      faq: [
        {
          q: "Quels documents sont nécessaires pour louer ?",
          a: "Permis valide, pièce d'identité et justificatifs selon le type de véhicule et la durée choisie.",
        },
        {
          q: "Puis-je ajouter un chauffeur ?",
          a: "Oui, nos offres peuvent inclure un chauffeur privé pour une partie ou la totalité de votre réservation.",
        },
        {
          q: "Quelle catégorie choisir pour un événement ?",
          a: "Prestige pour impact image, SUV pour polyvalence, cabriolet pour expérience loisir. Nous vous guidons selon votre objectif.",
        },
      ],
      footerLinks: [
        { label: "Voir la gamme prestige", href: "/location/prestige" },
        { label: "Passer en longue durée", href: "/location/longue-duree" },
        { label: "Comprendre la courte durée", href: "/location/courte-duree" },
        { label: "Voir les offres location", href: "/location/offres" },
      ],
    },
    catalogue: {
      back: "Retour en arrière",
      backHref: "/location",
      title: "Tous nos véhicules",
      intro: "Parcourez tous nos véhicules d'exception",
      filters: "Filtres",
      closeFilter: "Fermer le filtre",
      brands: "Marques",
      priceLabel: "Prix (1 a 3 jours)",
      searchPlaceholder: "Rechercher un modèle, une marque...",
      sortedBy: "Classé par :",
      sortOptions: {
        "date-desc": "Date - La plus récente",
        "date-asc": "Date - La plus ancienne",
        "price-desc": "Prix - Le plus élevé",
        "price-asc": "Prix - Le plus bas",
        popularity: "Popularité",
      },
      view2: "Affichage en 2 colonnes",
      view3: "Affichage en 3 colonnes",
      available: (n) =>
        `${n} véhicule${n > 1 ? "s" : ""} disponible${n > 1 ? "s" : ""}`,
      emptyTitle: "Aucun véhicule ne correspond à ces critères",
      emptyText:
        "Ajustez les filtres ou effacez la recherche pour voir toute la flotte.",
      reset: "Réinitialiser les filtres",
      bookThis: "Réserver ce véhicule",
    },
    card: {
      newBadge: "Nouveau",
      driverBadge: "Chauffeur",
      reserve: "Reserver",
      fromHourly: (price) => `A partir de ${price}/h`,
      perDay: "/j",
      kmPerDay: (km) => `${km} km inclus / jour`,
      detailsAria: (name) => `Voir les details du vehicule ${name}`,
    },
    form: {
      eyebrow: "Reservation rapide",
      heading: "Finaliser votre location en 1 formulaire",
      intro:
        "Choisissez votre modele, laissez vos coordonnees et recevez une confirmation rapide par telephone ou email. Le vehicule est preselectionne depuis le catalogue.",
      selectedModel: "Modele selectionne :",
      perDay: "/jour",
      priceForDuration: "Prix retenu pour la duree choisie :",
      seeCatalogue: "Voir le catalogue",
      seeModel: "Voir le modele selectionne",
      fullName: "Nom complet",
      email: "Email",
      phone: "Telephone",
      phonePlaceholder: "+33 6 60 15 98 00",
      vehicle: "Vehicule souhaite",
      selectModel: "Selectionner un modele",
      duration: "Duree de location",
      date: "Date souhaitee",
      message: "Message",
      messagePlaceholder: "Besoin particulier (livraison, horaires, etc.)",
      submit: "Envoyer ma demande de location",
      sending: "Envoi...",
      success:
        "Demande envoyee. Notre equipe vous confirme les disponibilites et les prochaines etapes rapidement.",
      error:
        "Impossible d'envoyer la demande pour le moment. Reessayez dans un instant.",
      v: {
        nameReq: "Nom requis",
        emailReq: "Email requis",
        phoneReq: "Telephone requis",
        phoneInvalid: "Telephone invalide",
        vehicleReq: "Vehicule requis",
        durationReq: "Duree requise",
        messageReq: "Message requis",
      },
      phonePattern: FR_PHONE,
      subjectPrefix: "Demande location vehicule - ",
    },
  },
  en: {
    durationLabels: {
      oneToThreeDays: "1 to 3 days",
      fourToSixDays: "4 to 6 days",
      sevenPlusDays: "7 days +",
    },
    page: {
      crumbHome: "Home",
      crumbRental: "Rental",
      crumbVehicles: "Vehicles",
      eyebrow: "Premium catalogue",
      h1: "Premium vehicle rental in Cannes, Nice and Monaco",
      intro:
        "Quickly compare our categories, pick the model that fits your context (business, stay, event), then book with human support.",
      cta: "Choose a vehicle and book",
      faqHeading: "Vehicle rental FAQ",
      faq: [
        {
          q: "What documents do I need to rent?",
          a: "A valid driving licence, ID and supporting documents depending on the vehicle type and chosen duration.",
        },
        {
          q: "Can I add a chauffeur?",
          a: "Yes, our offers can include a private chauffeur for part or all of your booking.",
        },
        {
          q: "Which category should I choose for an event?",
          a: "Prestige for image impact, SUV for versatility, convertible for a leisure experience. We guide you to your goal.",
        },
      ],
      footerLinks: [
        {
          label: "See the prestige range",
          href: "/en/location/prestige-car-rental",
        },
        {
          label: "Switch to long-term",
          href: "/en/location/long-term-car-rental",
        },
        {
          label: "Understand short-term",
          href: "/en/location/short-term-car-rental",
        },
        { label: "See rental offers", href: "/en/location/rental-offers" },
      ],
    },
    catalogue: {
      back: "Go back",
      backHref: "/location",
      title: "All our vehicles",
      intro: "Browse all our exceptional vehicles",
      filters: "Filters",
      closeFilter: "Close filter",
      brands: "Brands",
      priceLabel: "Price (1 to 3 days)",
      searchPlaceholder: "Search a model, a brand...",
      sortedBy: "Sorted by:",
      sortOptions: {
        "date-desc": "Date - Newest",
        "date-asc": "Date - Oldest",
        "price-desc": "Price - Highest",
        "price-asc": "Price - Lowest",
        popularity: "Popularity",
      },
      view2: "Display in 2 columns",
      view3: "Display in 3 columns",
      available: (n) => `${n} vehicle${n > 1 ? "s" : ""} available`,
      emptyTitle: "No vehicle matches these criteria",
      emptyText:
        "Adjust the filters or clear the search to see the whole fleet.",
      reset: "Reset filters",
      bookThis: "Book this vehicle",
    },
    card: {
      newBadge: "New",
      driverBadge: "Chauffeur",
      reserve: "Book",
      fromHourly: (price) => `From ${price}/h`,
      perDay: "/day",
      kmPerDay: (km) => `${km} km included / day`,
      detailsAria: (name) => `View details of ${name}`,
    },
    form: {
      eyebrow: "Quick booking",
      heading: "Complete your rental in 1 form",
      intro:
        "Choose your model, leave your details and get a fast confirmation by phone or email. The vehicle is preselected from the catalogue.",
      selectedModel: "Selected model:",
      perDay: "/day",
      priceForDuration: "Price for the chosen duration:",
      seeCatalogue: "See the catalogue",
      seeModel: "See the selected model",
      fullName: "Full name",
      email: "Email",
      phone: "Phone",
      phonePlaceholder: "+1 555 010 1234",
      vehicle: "Desired vehicle",
      selectModel: "Select a model",
      duration: "Rental duration",
      date: "Preferred date",
      message: "Message",
      messagePlaceholder: "Specific need (delivery, times, etc.)",
      submit: "Send my rental request",
      sending: "Sending...",
      success:
        "Request sent. Our team will quickly confirm availability and the next steps.",
      error:
        "Couldn't send the request right now. Please try again in a moment.",
      v: {
        nameReq: "Name required",
        emailReq: "Email required",
        phoneReq: "Phone required",
        phoneInvalid: "Invalid phone number",
        vehicleReq: "Vehicle required",
        durationReq: "Duration required",
        messageReq: "Message required",
      },
      phonePattern: INTL_PHONE,
      subjectPrefix: "🇬🇧 [EN CLIENT] Car rental request - ",
    },
  },
  ar: {
    durationLabels: {
      oneToThreeDays: "1 إلى 3 أيام",
      fourToSixDays: "4 إلى 6 أيام",
      sevenPlusDays: "7 أيام فأكثر",
    },
    page: {
      crumbHome: "الرئيسية",
      crumbRental: "التأجير",
      crumbVehicles: "السيارات",
      eyebrow: "كتالوج فاخر",
      h1: "تأجير سيارات فاخرة في كان ونيس وموناكو",
      intro:
        "قارن بسرعة بين فئاتنا، اختر الطراز الذي يلائم سياقك (أعمال، إقامة، مناسبة)، ثم احجز بمرافقة بشرية.",
      cta: "اختر سيارة واحجز",
      faqHeading: "الأسئلة الشائعة حول تأجير السيارات",
      faq: [
        {
          q: "ما المستندات اللازمة للتأجير؟",
          a: "رخصة قيادة سارية، وثيقة هوية، ومستندات داعمة بحسب نوع السيارة والمدّة المختارة.",
        },
        {
          q: "هل يمكنني إضافة سائق؟",
          a: "نعم، يمكن أن تتضمّن عروضنا سائقاً خاصاً لجزء من حجزك أو لكامله.",
        },
        {
          q: "أيّ فئة أختار لمناسبة؟",
          a: "الفخامة لأثر الصورة، الدفع الرباعي لتعدّد الاستخدام، الكابريوليه لتجربة الترفيه. نوجّهك بحسب هدفك.",
        },
      ],
      footerLinks: [
        { label: "اطّلع على فئة الفخامة", href: "/ar/location/prestige" },
        { label: "انتقل إلى المدى الطويل", href: "/ar/location/longue-duree" },
        { label: "افهم المدى القصير", href: "/ar/location/courte-duree" },
        { label: "اطّلع على عروض التأجير", href: "/ar/location/offres" },
      ],
    },
    catalogue: {
      back: "العودة",
      backHref: "/ar/location",
      title: "جميع سياراتنا",
      intro: "تصفّح جميع سياراتنا الاستثنائية",
      filters: "عوامل التصفية",
      closeFilter: "إغلاق التصفية",
      brands: "العلامات",
      priceLabel: "السعر (1 إلى 3 أيام)",
      searchPlaceholder: "ابحث عن طراز أو علامة...",
      sortedBy: "مرتّب حسب:",
      sortOptions: {
        "date-desc": "التاريخ - الأحدث",
        "date-asc": "التاريخ - الأقدم",
        "price-desc": "السعر - الأعلى",
        "price-asc": "السعر - الأدنى",
        popularity: "الشهرة",
      },
      view2: "عرض في عمودين",
      view3: "عرض في ثلاثة أعمدة",
      available: (n) => `${n} سيارة متاحة`,
      emptyTitle: "لا توجد سيارة تطابق هذه المعايير",
      emptyText: "عدّل عوامل التصفية أو امسح البحث لعرض كامل الأسطول.",
      reset: "إعادة ضبط عوامل التصفية",
      bookThis: "احجز هذه السيارة",
    },
    card: {
      newBadge: "جديد",
      driverBadge: "سائق",
      reserve: "احجز",
      fromHourly: (price) => `ابتداءً من ${price}/ساعة`,
      perDay: "/يوم",
      kmPerDay: (km) => `${km} كم مشمولة / يوم`,
      detailsAria: (name) => `اطّلع على تفاصيل سيارة ${name}`,
    },
    form: {
      eyebrow: "حجز سريع",
      heading: "أكمل تأجيرك بنموذج واحد",
      intro:
        "اختر طرازك، اترك بياناتك، واحصل على تأكيد سريع عبر الهاتف أو البريد الإلكتروني. السيارة محدّدة مسبقاً من الكتالوج.",
      selectedModel: "الطراز المختار:",
      perDay: "/يوم",
      priceForDuration: "السعر المعتمد للمدّة المختارة:",
      seeCatalogue: "اطّلع على الكتالوج",
      seeModel: "اطّلع على الطراز المختار",
      fullName: "الاسم الكامل",
      email: "البريد الإلكتروني",
      phone: "الهاتف",
      phonePlaceholder: "+33 6 60 15 98 00",
      vehicle: "السيارة المرغوبة",
      selectModel: "اختر طرازاً",
      duration: "مدّة التأجير",
      date: "التاريخ المرغوب",
      message: "الرسالة",
      messagePlaceholder: "حاجة خاصة (توصيل، مواعيد، إلخ)",
      submit: "أرسل طلب التأجير",
      sending: "جارٍ الإرسال...",
      success:
        "تم إرسال الطلب. سيؤكّد لك فريقنا التوافر والخطوات التالية بسرعة.",
      error: "تعذّر إرسال الطلب في الوقت الحالي. أعد المحاولة بعد لحظات.",
      v: {
        nameReq: "الاسم مطلوب",
        emailReq: "البريد الإلكتروني مطلوب",
        phoneReq: "الهاتف مطلوب",
        phoneInvalid: "رقم هاتف غير صالح",
        vehicleReq: "السيارة مطلوبة",
        durationReq: "المدّة مطلوبة",
        messageReq: "الرسالة مطلوبة",
      },
      phonePattern: INTL_PHONE,
      subjectPrefix: "🇸🇦 [AR CLIENT] طلب تأجير سيارة - ",
    },
  },
};
